import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import Icon from "./Icon";
import { EmptyState } from "./State";

// Thread-safe force layout calculation with min distance enforcement between connected nodes
function computeInitialPositions(nodes, edges, width = 800, height = 450) {
  const coords = {};
  if (!nodes || nodes.length === 0) return coords;

  const n = nodes.length;
  const centerX = width / 2;
  const centerY = height / 2;

  if (n === 1) {
    coords[nodes[0].id] = { x: centerX, y: centerY };
    return coords;
  }

  // Generous initial circle radius so nodes start with plenty of separation
  const radius = Math.max(220, Math.min(420, n * 65));
  nodes.forEach((node, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    coords[node.id] = {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  });

  // Synchronous Force Simulation (80 iterations max, ensures ideal node distance ~260-280px)
  const iterations = 80;
  const idealEdgeLength = 260;
  const minNodeDist = 280;

  for (let iter = 0; iter < iterations; iter++) {
    const tempDisp = {};
    nodes.forEach((node) => {
      tempDisp[node.id] = { dx: 0, dy: 0 };
    });

    // Node-to-Node Repulsion (keeps node cards comfortably separated)
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const n1 = nodes[i];
        const n2 = nodes[j];
        const p1 = coords[n1.id];
        const p2 = coords[n2.id];
        let dx = p1.x - p2.x;
        let dy = p1.y - p2.y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;
        if (dist < minNodeDist) {
          const force = (minNodeDist - dist) * 0.4;
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          tempDisp[n1.id].dx += fx;
          tempDisp[n1.id].dy += fy;
          tempDisp[n2.id].dx -= fx;
          tempDisp[n2.id].dy -= fy;
        }
      }
    }

    // Edge Spring Forces (maintains optimal distance between connected nodes)
    edges.forEach((edge) => {
      const p1 = coords[edge.source];
      const p2 = coords[edge.target];
      if (p1 && p2) {
        let dx = p1.x - p2.x;
        let dy = p1.y - p2.y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const delta = dist - idealEdgeLength;
        const force = delta * 0.05;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        if (tempDisp[edge.source]) {
          tempDisp[edge.source].dx -= fx;
          tempDisp[edge.source].dy -= fy;
        }
        if (tempDisp[edge.target]) {
          tempDisp[edge.target].dx += fx;
          tempDisp[edge.target].dy += fy;
        }
      }
    });

    const damping = Math.max(0.1, 1 - iter / iterations);
    nodes.forEach((node) => {
      const disp = tempDisp[node.id];
      const dDist = Math.sqrt(disp.dx * disp.dx + disp.dy * disp.dy) || 1;
      const maxDisp = 35 * damping;
      const limitedDist = Math.min(dDist, maxDisp);
      coords[node.id].x += (disp.dx / dDist) * limitedDist;
      coords[node.id].y += (disp.dy / dDist) * limitedDist;
    });
  }

  return coords;
}

export function TopologyView({ topology }) {
  const [viewMode, setViewMode] = useState("graph"); // "graph" or "table"
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [nodeCoords, setNodeCoords] = useState({});
  const [zoomState, setZoomState] = useState({ zoom: 1, pan: { x: 0, y: 0 } });
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [isPanningCanvas, setIsPanningCanvas] = useState(false);

  const containerRef = useRef(null);
  const dragInfoRef = useRef(null);
  const touchesRef = useRef([]);
  const fitViewRef = useRef(null);
  const zoomStateRef = useRef(zoomState);

  useEffect(() => {
    zoomStateRef.current = zoomState;
  }, [zoomState]);

  // Filter server nodes and edges
  const nodes = topology?.nodes || [];
  const edges = topology?.edges || [];
  const nodesMap = Object.fromEntries(nodes.map((n) => [n.id, n]));

  const serverNodes = nodes.filter((n) => n.type === "server");
  const displayNodes = serverNodes.length > 0 ? serverNodes : nodes;

  const serviceToServerMap = {};
  nodes
    .filter((n) => n.type === "service")
    .forEach((n) => {
      serviceToServerMap[n.id] = n.metadata?.server_id
        ? `server:${n.metadata.server_id}`
        : null;
    });

  const serverEdgesMap = {};
  edges.forEach((e) => {
    const sourceServerId = serviceToServerMap[e.source] || e.source;
    const targetServerId = serviceToServerMap[e.target] || e.target;
    if (sourceServerId && targetServerId && sourceServerId !== targetServerId) {
      const edgeKey = `${sourceServerId}->${targetServerId}`;
      if (!serverEdgesMap[edgeKey]) {
        serverEdgesMap[edgeKey] = {
          id: edgeKey,
          source: sourceServerId,
          target: targetServerId,
          connections: [],
        };
      }
      serverEdgesMap[edgeKey].connections.push(e);
    }
  });
  const serverEdges = Object.values(serverEdgesMap);

  // Initialize node positions when topology changes
  useEffect(() => {
    if (!topology || displayNodes.length === 0) return;
    const initialCoords = computeInitialPositions(displayNodes, serverEdges);
    setNodeCoords(initialCoords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topology]);

  const formatNodeLabel = (nodeId) => {
    const node = nodesMap[nodeId];
    if (!node) return nodeId;

    if (node.type === "service") {
      const serverId = node.metadata?.server_id;
      const serverNode = serverId ? nodesMap[`server:${serverId}`] : null;
      return serverNode ? `${node.label} (${serverNode.label})` : node.label;
    }
    return node.label;
  };

  // Computes precise intersection distance with node card bounding box (150x50) + margin for arrowhead
  const getNodeBorderOffset = (dx, dy, len) => {
    if (!len || len === 0) return 50;
    const boxWidthHalf = 75 + 12; // half width (75px) + arrowhead margin (12px)
    const boxHeightHalf = 25 + 12; // half height (25px) + arrowhead margin (12px)
    const absDx = Math.abs(dx / len);
    const absDy = Math.abs(dy / len);

    return Math.min(
      absDx > 0.001 ? boxWidthHalf / absDx : Infinity,
      absDy > 0.001 ? boxHeightHalf / absDy : Infinity,
    );
  };

  const getEdgePath = (sourceId, targetId) => {
    const p1 = nodeCoords[sourceId];
    const p2 = nodeCoords[targetId];
    if (!p1 || !p2) return "";

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const offset1 = getNodeBorderOffset(dx, dy, len);
    const offset2 = getNodeBorderOffset(-dx, -dy, len);

    const startX = p1.x + (dx / len) * offset1;
    const startY = p1.y + (dy / len) * offset1;
    const endX = p2.x - (dx / len) * offset2;
    const endY = p2.y - (dy / len) * offset2;

    const hasReverse = serverEdges.some(
      (e) => e.source === targetId && e.target === sourceId,
    );

    if (hasReverse) {
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      const nx = -dy / len;
      const ny = dx / len;
      const offset = 26;
      const cx = midX + nx * offset;
      const cy = midY + ny * offset;
      return `M ${startX} ${startY} Q ${cx} ${cy} ${endX} ${endY}`;
    }

    return `M ${startX} ${startY} L ${endX} ${endY}`;
  };

  // Fit View: Bounds all nodes within the visible container with responsive padding
  const fitView = useCallback(() => {
    if (!containerRef.current) return;
    const containerW = containerRef.current.clientWidth || 800;
    const containerH = containerRef.current.clientHeight || 480;

    const nodeIds = Object.keys(nodeCoords);
    if (nodeIds.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodeIds.forEach((id) => {
      const { x, y } = nodeCoords[id];
      if (x - 85 < minX) minX = x - 85;
      if (x + 85 > maxX) maxX = x + 85;
      if (y - 40 < minY) minY = y - 40;
      if (y + 40 > maxY) maxY = y + 40;
    });

    if (minX === Infinity || maxX === -Infinity) return;

    const graphW = Math.max(maxX - minX, 120);
    const graphH = Math.max(maxY - minY, 120);
    const graphCenterX = (minX + maxX) / 2;
    const graphCenterY = (minY + maxY) / 2;

    // Responsive padding based on viewport size
    const padding = containerW < 600 ? 30 : 60;
    const availW = Math.max(containerW - padding * 2, 100);
    const availH = Math.max(containerH - padding * 2, 100);

    const scaleX = availW / graphW;
    const scaleY = availH / graphH;
    let fittedScale = Math.min(scaleX, scaleY);
    fittedScale = Math.min(Math.max(fittedScale, 0.2), 1.5);

    const targetPanX = containerW / 2 - graphCenterX * fittedScale;
    const targetPanY = containerH / 2 - graphCenterY * fittedScale;

    setZoomState({
      zoom: fittedScale,
      pan: { x: targetPanX, y: targetPanY },
    });
  }, [nodeCoords]);

  useEffect(() => {
    fitViewRef.current = fitView;
  }, [fitView]);

  // Fit view automatically after initial layout or DOM reflow finishes
  useLayoutEffect(() => {
    if (viewMode !== "graph" || !containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          requestAnimationFrame(() => {
            if (fitViewRef.current) fitViewRef.current();
          });
        }
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [viewMode]);

  // Handle Firefox & Chrome wheel events with normalized deltaMode
  useEffect(() => {
    const container = containerRef.current;
    if (!container || viewMode !== "graph") return;

    const handleWheel = (e) => {
      e.preventDefault();

      let delta = e.deltaY;
      if (e.deltaMode === 1) {
        // DOM_DELTA_LINE in Firefox
        delta *= 20;
      } else if (e.deltaMode === 2) {
        // DOM_DELTA_PAGE
        delta *= 800;
      }

      const zoomFactor = Math.exp(-delta * 0.0015);
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      setZoomState((prev) => {
        const newScale = Math.min(Math.max(prev.zoom * zoomFactor, 0.15), 4.0);
        if (newScale === prev.zoom) return prev;

        const newPanX = mouseX - (mouseX - prev.pan.x) * (newScale / prev.zoom);
        const newPanY = mouseY - (mouseY - prev.pan.y) * (newScale / prev.zoom);

        return {
          zoom: newScale,
          pan: { x: newPanX, y: newPanY },
        };
      });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [viewMode]);

  // Multi-touch gestures for pinch zoom & touch panning
  useEffect(() => {
    const container = containerRef.current;
    if (!container || viewMode !== "graph") return;

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        touchesRef.current = [
          { x: e.touches[0].clientX, y: e.touches[0].clientY },
          { x: e.touches[1].clientX, y: e.touches[1].clientY },
        ];
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && touchesRef.current.length === 2) {
        e.preventDefault();
        const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };

        const prevDist =
          Math.hypot(
            touchesRef.current[0].x - touchesRef.current[1].x,
            touchesRef.current[0].y - touchesRef.current[1].y,
          ) || 1;

        const newDist = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
        const zoomFactor = newDist / prevDist;

        const rect = container.getBoundingClientRect();
        const centerX = (p1.x + p2.x) / 2 - rect.left;
        const centerY = (p1.y + p2.y) / 2 - rect.top;

        setZoomState((prev) => {
          const newScale = Math.min(
            Math.max(prev.zoom * zoomFactor, 0.15),
            4.0,
          );
          const newPanX =
            centerX - (centerX - prev.pan.x) * (newScale / prev.zoom);
          const newPanY =
            centerY - (centerY - prev.pan.y) * (newScale / prev.zoom);
          return { zoom: newScale, pan: { x: newPanX, y: newPanY } };
        });

        touchesRef.current = [p1, p2];
      }
    };

    const handleTouchEnd = () => {
      touchesRef.current = [];
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [viewMode]);

  // Pointer drag logic for nodes & canvas panning
  const handleNodePointerDown = (e, nodeId) => {
    e.stopPropagation();
    if (e.button !== undefined && e.button !== 0) return;

    const coords = nodeCoords[nodeId];
    if (!coords) return;

    dragInfoRef.current = {
      type: "node",
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      initialNodeX: coords.x,
      initialNodeY: coords.y,
    };
    setIsDraggingNode(true);
  };

  const handleCanvasPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;

    dragInfoRef.current = {
      type: "pan",
      startX: e.clientX,
      startY: e.clientY,
      initialPanX: zoomStateRef.current.pan.x,
      initialPanY: zoomStateRef.current.pan.y,
    };
    setIsPanningCanvas(true);
  };

  useEffect(() => {
    const handleGlobalPointerMove = (e) => {
      if (!dragInfoRef.current) return;

      if (dragInfoRef.current.type === "node") {
        const currentZoom = zoomStateRef.current.zoom || 1;
        const dx = (e.clientX - dragInfoRef.current.startX) / currentZoom;
        const dy = (e.clientY - dragInfoRef.current.startY) / currentZoom;
        const targetId = dragInfoRef.current.nodeId;

        setNodeCoords((prev) => ({
          ...prev,
          [targetId]: {
            x: dragInfoRef.current.initialNodeX + dx,
            y: dragInfoRef.current.initialNodeY + dy,
          },
        }));
      } else if (dragInfoRef.current.type === "pan") {
        const dx = e.clientX - dragInfoRef.current.startX;
        const dy = e.clientY - dragInfoRef.current.startY;

        setZoomState((prev) => ({
          ...prev,
          pan: {
            x: dragInfoRef.current.initialPanX + dx,
            y: dragInfoRef.current.initialPanY + dy,
          },
        }));
      }
    };

    const handleGlobalPointerUp = () => {
      if (dragInfoRef.current) {
        dragInfoRef.current = null;
        setIsDraggingNode(false);
        setIsPanningCanvas(false);
      }
    };

    window.addEventListener("pointermove", handleGlobalPointerMove);
    window.addEventListener("pointerup", handleGlobalPointerUp);
    window.addEventListener("pointercancel", handleGlobalPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);
      window.removeEventListener("pointercancel", handleGlobalPointerUp);
    };
  }, []);

  // Floating controls zoom actions
  const handleZoomIn = () => {
    if (!containerRef.current) return;
    const containerW = containerRef.current.clientWidth || 800;
    const containerH = containerRef.current.clientHeight || 480;
    const centerX = containerW / 2;
    const centerY = containerH / 2;

    setZoomState((prev) => {
      const newScale = Math.min(prev.zoom * 1.25, 4.0);
      const newPanX = centerX - (centerX - prev.pan.x) * (newScale / prev.zoom);
      const newPanY = centerY - (centerY - prev.pan.y) * (newScale / prev.zoom);
      return { zoom: newScale, pan: { x: newPanX, y: newPanY } };
    });
  };

  const handleZoomOut = () => {
    if (!containerRef.current) return;
    const containerW = containerRef.current.clientWidth || 800;
    const containerH = containerRef.current.clientHeight || 480;
    const centerX = containerW / 2;
    const centerY = containerH / 2;

    setZoomState((prev) => {
      const newScale = Math.max(prev.zoom / 1.25, 0.15);
      const newPanX = centerX - (centerX - prev.pan.x) * (newScale / prev.zoom);
      const newPanY = centerY - (centerY - prev.pan.y) * (newScale / prev.zoom);
      return { zoom: newScale, pan: { x: newPanX, y: newPanY } };
    });
  };

  if (!topology || !topology.nodes || topology.nodes.length === 0)
    return (
      <EmptyState
        icon="network"
        title="No network topology"
        subtitle="No active servers or network connections detected to map."
      />
    );

  const cssAnimations = `
    @keyframes flow {
      from { stroke-dashoffset: 20; }
      to { stroke-dashoffset: 0; }
    }
    .flow-line {
      stroke-dasharray: 6, 4;
      animation: flow 1.2s linear infinite;
    }
    @keyframes pulse-dot {
      0% { transform: scale(0.9); opacity: 0.6; }
      50% { transform: scale(1.3); opacity: 1; }
      100% { transform: scale(0.9); opacity: 0.6; }
    }
    .pulse-dot {
      animation: pulse-dot 2s infinite ease-in-out;
    }
  `;

  return (
    <div style={{ width: "100%" }}>
      <style dangerouslySetInnerHTML={{ __html: cssAnimations }} />

      {/* Header section inside the Topology Tab */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 700,
              color: "#1E293B",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="network" size={15} color="#1D4ED8" />
            Network Topology & Dependencies
          </h2>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748B" }}>
            Interactive map of active connections and service dependencies. Pan,
            zoom, or drag nodes to adjust the graph layout.
          </p>
        </div>

        {/* View Mode Switcher */}
        <div
          style={{
            background: "#E2E8F0",
            padding: 3,
            borderRadius: 8,
            display: "flex",
            gap: 2,
            alignSelf: "flex-start",
          }}
        >
          <button
            onClick={() => setViewMode("graph")}
            style={{
              padding: "4px 12px",
              fontSize: 11,
              borderRadius: 6,
              cursor: "pointer",
              border: "none",
              fontWeight: 600,
              background: viewMode === "graph" ? "#fff" : "none",
              color: viewMode === "graph" ? "#1D4ED8" : "#64748B",
              boxShadow:
                viewMode === "graph" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.15s ease",
            }}
          >
            Graph View
          </button>
          <button
            onClick={() => setViewMode("table")}
            style={{
              padding: "4px 12px",
              fontSize: 11,
              borderRadius: 6,
              cursor: "pointer",
              border: "none",
              fontWeight: 600,
              background: viewMode === "table" ? "#fff" : "none",
              color: viewMode === "table" ? "#1D4ED8" : "#64748B",
              boxShadow:
                viewMode === "table" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.15s ease",
            }}
          >
            Connections Table
          </button>
        </div>
      </div>

      {viewMode === "graph" ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            border: "1px solid #E2E8F0",
            padding: "12px 12px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            width: "100%",
          }}
        >
          {/* SVG Canvas Wrapper Container with Responsive Height & Floating Navigation Controls */}
          <div
            ref={containerRef}
            style={{
              position: "relative",
              width: "100%",
              height: "clamp(340px, 58vh, 520px)",
              overflow: "hidden",
              borderRadius: 8,
              border: "1px solid #F1F5F9",
              background:
                "url(\"data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1' fill='%23E2E8F0'/%3E%3C/svg%3E\")",
              cursor: isPanningCanvas
                ? "grabbing"
                : isDraggingNode
                  ? "grabbing"
                  : "grab",
              userSelect: "none",
            }}
          >
            {/* On-Screen Pan/Zoom Navigation Controls */}
            <div
              style={{
                position: "absolute",
                bottom: 10,
                right: 10,
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "#FFFFFF",
                border: "1px solid #CBD5E1",
                borderRadius: 8,
                padding: "3px 4px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                zIndex: 10,
                maxWidth: "calc(100% - 20px)",
              }}
            >
              <button
                type="button"
                title="Zoom In (+)"
                aria-label="Zoom In"
                onClick={handleZoomIn}
                style={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                  border: "none",
                  background: "#F8FAFC",
                  color: "#1E293B",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
              >
                +
              </button>
              <button
                type="button"
                title="Zoom Out (-)"
                aria-label="Zoom Out"
                onClick={handleZoomOut}
                style={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                  border: "none",
                  background: "#F8FAFC",
                  color: "#1E293B",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
              >
                -
              </button>
              <div
                style={{
                  padding: "0 4px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#64748B",
                  minWidth: 38,
                  textAlign: "center",
                }}
              >
                {Math.round(zoomState.zoom * 100)}%
              </div>
              <button
                type="button"
                title="Fit Graph to Screen (⛶)"
                aria-label="Fit Graph to Screen"
                onClick={fitView}
                style={{
                  height: 28,
                  padding: "0 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  borderRadius: 6,
                  border: "none",
                  background: "#EFF6FF",
                  color: "#1D4ED8",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "background 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 13 }}>⛶</span> Fit
              </button>
            </div>

            {/* Interactive SVG Workspace */}
            <svg
              width="100%"
              height="100%"
              style={{ touchAction: "none" }}
              onPointerDown={handleCanvasPointerDown}
            >
              <defs>
                <filter
                  id="glow-hover"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                >
                  <feDropShadow
                    dx="0"
                    dy="3"
                    stdDeviation="5"
                    floodColor="#3B82F6"
                    floodOpacity="0.25"
                  />
                </filter>
                <marker
                  id="arrow"
                  viewBox="0 0 10 10"
                  refX="6"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#94A3B8" />
                </marker>
                <marker
                  id="arrow-hover"
                  viewBox="0 0 10 10"
                  refX="6"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#1D4ED8" />
                </marker>
              </defs>

              <g
                transform={`translate(${zoomState.pan.x}, ${zoomState.pan.y}) scale(${zoomState.zoom})`}
              >
                {/* Connection Edges */}
                {serverEdges.map((edge) => {
                  const isSelected = hoveredEdge?.id === edge.id;
                  const isRelatedToHoveredNode =
                    hoveredNode &&
                    (edge.source === hoveredNode ||
                      edge.target === hoveredNode);
                  const path = getEdgePath(edge.source, edge.target);

                  return (
                    <g key={edge.id}>
                      <path
                        d={path}
                        fill="none"
                        stroke="transparent"
                        strokeWidth="12"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setHoveredEdge(edge)}
                        onMouseLeave={() => setHoveredEdge(null)}
                      />
                      <path
                        d={path}
                        fill="none"
                        stroke={
                          isSelected
                            ? "#1D4ED8"
                            : isRelatedToHoveredNode
                              ? "#3B82F6"
                              : "#CBD5E1"
                        }
                        strokeWidth={
                          isSelected ? 3 : isRelatedToHoveredNode ? 2 : 1.5
                        }
                        markerEnd={`url(#${isSelected ? "arrow-hover" : "arrow"})`}
                        style={{
                          transition: "stroke 0.2s, stroke-width 0.2s",
                          pointerEvents: "none",
                        }}
                      />
                      {(isSelected || isRelatedToHoveredNode) && (
                        <path
                          d={path}
                          fill="none"
                          stroke={isSelected ? "#1D4ED8" : "#3B82F6"}
                          strokeWidth={2}
                          className="flow-line"
                          style={{ pointerEvents: "none" }}
                        />
                      )}
                      <circle
                        r="4.5"
                        fill={isSelected ? "#1D4ED8" : "#3B82F6"}
                        filter="url(#glow-hover)"
                      >
                        <animateMotion
                          dur="2.5s"
                          repeatCount="indefinite"
                          path={path}
                        />
                      </circle>
                    </g>
                  );
                })}

                {/* Server Nodes */}
                {displayNodes.map((node) => {
                  const coords = nodeCoords[node.id];
                  if (!coords) return null;

                  const isHovered = hoveredNode === node.id;
                  const isRelated =
                    hoveredNode &&
                    (serverEdges.some(
                      (e) => e.source === hoveredNode && e.target === node.id,
                    ) ||
                      serverEdges.some(
                        (e) => e.target === hoveredNode && e.source === node.id,
                      ));

                  const isActive = node.status === "active";
                  const statusColor = isActive ? "#10B981" : "#EF4444";
                  const statusBg = isActive ? "#ECFDF5" : "#FEF2F2";
                  const statusBorder = isActive ? "#A7F3D0" : "#FCA5A5";

                  return (
                    <g
                      key={node.id}
                      transform={`translate(${coords.x}, ${coords.y})`}
                      onPointerDown={(e) => handleNodePointerDown(e, node.id)}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                      style={{ cursor: "grab" }}
                    >
                      <rect
                        x="-75"
                        y="-25"
                        width="150"
                        height="50"
                        rx="10"
                        fill="#FFFFFF"
                        stroke={
                          isHovered
                            ? "#1D4ED8"
                            : isRelated
                              ? "#3B82F6"
                              : "#E2E8F0"
                        }
                        strokeWidth={isHovered ? 2 : 1.5}
                        filter={isHovered ? "url(#glow-hover)" : "none"}
                        style={{ transition: "stroke 0.2s, filter 0.2s" }}
                      />
                      <path
                        d="M -65 -25 L 65 -25"
                        stroke={isActive ? "#3B82F6" : "#EF4444"}
                        strokeWidth="3"
                        strokeLinecap="round"
                      />

                      <g transform="translate(-10, 0)">
                        <rect
                          x="-52"
                          y="-12"
                          width="16"
                          height="4"
                          rx="1"
                          fill="#E2E8F0"
                          stroke="#94A3B8"
                          strokeWidth="0.5"
                        />
                        <circle
                          cx="-39"
                          cy="-10"
                          r="1"
                          fill={isActive ? "#10B981" : "#EF4444"}
                        />
                        <rect
                          x="-52"
                          y="-5"
                          width="16"
                          height="4"
                          rx="1"
                          fill="#E2E8F0"
                          stroke="#94A3B8"
                          strokeWidth="0.5"
                        />
                        <circle
                          cx="-39"
                          cy="-3"
                          r="1"
                          fill={isActive ? "#10B981" : "#EF4444"}
                        />
                        <rect
                          x="-52"
                          y="2"
                          width="16"
                          height="4"
                          rx="1"
                          fill="#E2E8F0"
                          stroke="#94A3B8"
                          strokeWidth="0.5"
                        />
                        <circle
                          cx="-39"
                          cy="4"
                          r="1"
                          fill={isActive ? "#10B981" : "#EF4444"}
                        />
                      </g>

                      <circle
                        cx="56"
                        cy="-10"
                        r="4"
                        fill={statusColor}
                        className="pulse-dot"
                        style={{ transformOrigin: "56px -10px" }}
                      />

                      <text
                        x="-38"
                        y="-2"
                        fontSize="11"
                        fontWeight="700"
                        fill="#1E293B"
                        textAnchor="start"
                      >
                        {node.label.length > 15
                          ? node.label.slice(0, 12) + "..."
                          : node.label}
                      </text>
                      <g transform="translate(-38, 6)">
                        <rect
                          x="0"
                          y="0"
                          width="42"
                          height="12"
                          rx="6"
                          fill={statusBg}
                          stroke={statusBorder}
                          strokeWidth="0.5"
                        />
                        <text
                          x="21"
                          y="9"
                          fontSize="8"
                          fontWeight="700"
                          fill={statusColor}
                          textAnchor="middle"
                        >
                          {(node.status || "active").toUpperCase()}
                        </text>
                      </g>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          {/* Interactive Info Footer */}
          <div style={{ width: "100%", padding: "12px 6px 0", minHeight: 48 }}>
            {hoveredEdge ? (
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  Connection Routes ({hoveredEdge.connections.length})
                </div>
                {hoveredEdge.connections.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ color: "#0F172A", fontWeight: 600 }}>
                      {formatNodeLabel(c.source)}
                    </span>
                    <span style={{ color: "#64748B" }}>
                      ({c.relation_type})
                    </span>
                    <strong style={{ color: "#2563EB" }}>→</strong>
                    <span style={{ color: "#0F172A", fontWeight: 600 }}>
                      {formatNodeLabel(c.target)}
                    </span>
                  </div>
                ))}
              </div>
            ) : hoveredNode ? (
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 11,
                  color: "#334155",
                }}
              >
                <strong>Server:</strong> {nodesMap[hoveredNode]?.label} (
                {nodesMap[hoveredNode]?.status})<br />
                <span style={{ fontSize: 10, color: "#64748B" }}>
                  OS: {nodesMap[hoveredNode]?.metadata?.os || "—"} | Cloud:{" "}
                  {nodesMap[hoveredNode]?.metadata?.hosted_on || "Local"}
                </span>
              </div>
            ) : (
              <div
                style={{
                  fontSize: 11,
                  color: "#64748B",
                  fontStyle: "italic",
                  textAlign: "center",
                  paddingTop: 8,
                }}
              >
                Hover over any connection line or server node to view routing
                details. Drag nodes, scroll, or pinch to zoom/pan.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Connections Table View Mode */
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            border: "1px solid #E2E8F0",
            overflowX: "auto",
            width: "100%",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}
        >
          <table
            style={{
              width: "100%",
              minWidth: 480,
              borderCollapse: "collapse",
              fontSize: 12,
              textAlign: "left",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#F8FAFC",
                  borderBottom: "1px solid #E2E8F0",
                  color: "#475569",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                <th style={{ padding: "10px 14px" }}>Source Server</th>
                <th style={{ padding: "10px 14px" }}>Target Server</th>
                <th style={{ padding: "10px 14px" }}>Connection Details</th>
              </tr>
            </thead>
            <tbody>
              {serverEdges.map((edge) => {
                const sourceNode = nodesMap[edge.source];
                const targetNode = nodesMap[edge.target];
                return (
                  <tr
                    key={edge.id}
                    style={{ borderBottom: "1px solid #F1F5F9" }}
                  >
                    <td
                      style={{
                        padding: "10px 14px",
                        fontWeight: 600,
                        color: "#0F172A",
                      }}
                    >
                      {sourceNode?.label || edge.source}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        fontWeight: 600,
                        color: "#0F172A",
                      }}
                    >
                      {targetNode?.label || edge.target}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        {edge.connections.map((c) => (
                          <div
                            key={c.id}
                            style={{
                              fontSize: 11,
                              color: "#475569",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span style={{ fontWeight: 600, color: "#1D4ED8" }}>
                              {formatNodeLabel(c.source)}
                            </span>
                            <span style={{ color: "#94A3B8" }}>
                              ({c.relation_type})
                            </span>
                            <strong>→</strong>
                            <span style={{ fontWeight: 600, color: "#1D4ED8" }}>
                              {formatNodeLabel(c.target)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TopologyView;
